import { TeamType } from '@modules/Auth/models/Auth';
import { UnauthorizedPage } from '@shared/components/auth/Unauthorized';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useAuthStore } from '@shared/stores/authStore';
import { useCRMEnabled } from '@modules/CRM/shared/hooks/useCRMSettings';
import { useProfitabilityAccess } from '@modules/Reports/Profitability/hooks/useProfitabilityAccess';
import React from 'react';

interface PermissionRouteProps {
  children: React.ReactNode;
  teamTypes?: TeamType[];
  permissions?: string[];
  requireAll?: boolean;
  requireRoleAdmin?: boolean;
  requireWalletVisible?: boolean;
  requireCRMEnabled?: boolean;
  /** Rentabilidad: solo si el hijo (external) o el flag de hijos en padre 3PL — ver useProfitabilityAccess. */
  requireProfitabilityAccess?: boolean;
}

/**
 * Wrapper para rutas que verifica teamTypes, permissions y requireRoleAdmin
 * Redirige a página de "Sin permisos" si no cumple los requisitos
 *
 * @example
 * ```tsx
 * <PermissionRoute
 *   teamTypes={['tenant', 'tenant_3pl']}
 *   permissions={['products.view']}
 *   requireRoleAdmin={true}
 * >
 *   <ProductsListPage />
 * </PermissionRoute>
 * ```
 */
const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  teamTypes,
  permissions,
  requireAll = false,
  requireRoleAdmin = false,
  requireWalletVisible = false,
  requireCRMEnabled = false,
  requireProfitabilityAccess = false,
}) => {
  const { canAccess, hasAllPermissions, isAdminRole, isWalletVisible } = usePermissions();
  const { token, role } = useAuthStore();
  const { crmEnabled } = useCRMEnabled();
  const profitabilityAccess = useProfitabilityAccess();

  // 🎯 Si hay token pero no hay role, está cargando - mostrar loading
  if (token && !role) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Verificar acceso
  let hasAccess = false;

  if (requireAll && permissions && permissions.length > 0) {
    // Verificar team_type Y todos los permisos
    hasAccess = canAccess(teamTypes, []) && hasAllPermissions(permissions);
  } else {
    // Verificación estándar: team_type + al menos uno de los permisos
    hasAccess = canAccess(teamTypes, permissions);
  }

  if (!hasAccess) {
    return <UnauthorizedPage />;
  }

  // Verificar requireRoleAdmin (después de teamTypes)
  if (requireRoleAdmin && !isAdminRole()) {
    return <UnauthorizedPage />;
  }

  // Verificar requireWalletVisible (para tenant_external)
  if (requireWalletVisible && !isWalletVisible()) {
    return <UnauthorizedPage />;
  }

  // Verificar requireCRMEnabled (si está definido)
  if (requireCRMEnabled && !crmEnabled) {
    return <UnauthorizedPage />;
  }

  if (requireProfitabilityAccess && !profitabilityAccess) {
    return <UnauthorizedPage />;
  }

  return <>{children}</>;
};

export default PermissionRoute;
